import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import type { DecorPreviewItem } from "@picnic-field/renderer";
import { courseDeps } from "../lib/deps";
import type { DecorPreviewLike } from "../types";

/**
 * 인벤토리 미리보기 창(2026-10-08 운영 무대 꾸밈 · 2026-10-11 자유 놀이터 배치 도구도) — 고른 것 하나를 빙글 돌려 보인다(렌더러 createDecorPreview).
 * 창마다 새 캔버스 — 같은 캔버스에 렌더러 둘(개발 모드 두 번 마운트)이 붙으면 앞엣것 해제가 뒤엣것 GL 상태를 깨뜨린다.
 * footer = 창 아래 한 줄(지금 보이는 물체 크기를 받는다 · 그릴 수 없으면 failText).
 */
export function ItemPreview({
  item,
  label,
  failText,
  testId,
  footer,
}: {
  item: DecorPreviewItem | null;
  /** 캔버스 이름(스크린 리더) */
  label: string;
  failText: string;
  testId: string;
  footer?: (size: { x: number; y: number; z: number } | null) => ReactNode;
}) {
  const boxRef = useRef<HTMLDivElement>(null);
  const handle = useRef<DecorPreviewLike | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "fail">("loading");
  const [size, setSize] = useState<{ x: number; y: number; z: number } | null>(null);

  useEffect(() => {
    const box = boxRef.current;
    if (!box) return;
    const canvas = document.createElement("canvas");
    canvas.className = "pf-item-preview__canvas";
    canvas.setAttribute("aria-label", label);
    box.prepend(canvas);
    let alive = true;
    courseDeps()
      .createDecorPreview(canvas)
      .then((h) => {
        if (!alive) return h.dispose();
        handle.current = h;
        setState("ready");
      })
      .catch(() => alive && setState("fail"));
    return () => {
      alive = false;
      handle.current?.dispose();
      handle.current = null;
      canvas.remove();
    };
    // label 은 이름표만
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const h = handle.current;
    if (state !== "ready" || !h) return;
    let alive = true;
    void h.show(item).then((ok) => alive && setSize(ok && item ? h.size() : null));
    return () => {
      alive = false;
    };
  }, [item, state]);

  return (
    <div ref={boxRef} className="pf-item-preview" data-view={state} data-testid={testId}>
      {state === "fail" ? <p className="pf-muted">{failText}</p> : footer?.(size)}
    </div>
  );
}
